import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Table,
  Loader,
  Center,
  Alert,
  Title,
  ScrollArea,
  Badge,
  Group,
  Text,
  ThemeIcon,
  Paper,
  Button,
  Avatar,
  Tooltip,
  ActionIcon,
  Modal, // <-- Tambahan untuk Modal
  Box,
  Divider,
  Grid
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks"; // <-- Tambahan Hook
import { 
  IconInfoCircle, 
  IconSoup, 
  IconRefresh, 
  IconChefHat,
  IconEye,
  IconCalendar,
  IconLeaf
} from "@tabler/icons-react";

export default function AdminMenuPage() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- STATE UNTUK MODAL DETAIL ---
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedMenu, setSelectedMenu] = useState(null);

  // --- 1. FETCH DATA ---
  const fetchMenus = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/menu");
      
      let hasilData = [];
      if (Array.isArray(response.data)) {
        hasilData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        hasilData = response.data.data;
      } else {
        hasilData = [];
      }

      setMenus(hasilData);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Gagal mengambil data menu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  // --- 2. HANDLER TOMBOL DETAIL ---
  const handleViewDetail = (menu) => {
    setSelectedMenu(menu); // Simpan data menu yang diklik
    open(); // Buka Modal
  };

  // --- 3. RENDER UI ---
  if (loading) return <Center h={300}><Loader size="lg" /></Center>;

  if (error) {
    return (
      <Alert icon={<IconInfoCircle />} title="Error" color="red" mt="md">
        {error}
        <Button variant="white" color="red" size="xs" mt="sm" onClick={fetchMenus}>
            Coba Lagi
        </Button>
      </Alert>
    );
  }

  // --- RENDER TABEL ---
  const rows = Array.isArray(menus) && menus.length > 0 ? (
    menus.map((item) => (
      <Table.Tr key={item.id}>
        <Table.Td>
          <Group gap="sm">
            <Avatar color="orange" radius="xl"><IconSoup size={20}/></Avatar>
            <div>
              <Text fz="sm" fw={500}>{item.nama_menu}</Text>
              <Text fz="xs" c="dimmed">
                {new Date(item.tanggal_menu).toLocaleDateString('id-ID', {
                    day: 'numeric', month: 'short', year: 'numeric'
                })}
              </Text>
            </div>
          </Group>
        </Table.Td>

        <Table.Td>
           <Group gap="xs">
             <ThemeIcon variant="light" color="blue" size="sm" radius="xl">
                <IconChefHat size={12}/>
             </ThemeIcon>
             <Text fz="sm">
                {/* Cek struktur relasi user/penyedia */}
                {item.penyedia?.user?.name || item.penyedia?.nama_perusahaan || "Penyedia Tidak Diketahui"}
             </Text>
           </Group>
        </Table.Td>

        <Table.Td>
           <Text fz="sm" lineClamp={1} c="dimmed" style={{maxWidth: 200}}>
              {item.deskripsi}
           </Text>
        </Table.Td>

        <Table.Td>
           <Badge variant="outline" color="gray">
              {item.bahan_makanans?.length || 0} Bahan
           </Badge>
        </Table.Td>

        <Table.Td>
           <Tooltip label="Lihat Detail & Resep">
              {/* --- DI SINI PERBAIKANNYA: Menambahkan onClick --- */}
              <ActionIcon variant="subtle" color="blue" onClick={() => handleViewDetail(item)}>
                 <IconEye size={16}/>
              </ActionIcon>
           </Tooltip>
        </Table.Td>
      </Table.Tr>
    ))
  ) : (
    <Table.Tr>
      <Table.Td colSpan={5}><Center p="md" c="dimmed">Belum ada data menu.</Center></Table.Td>
    </Table.Tr>
  );

  return (
    <>
      <Group justify="space-between" mb="lg">
        <div>
           <Title order={2}>Daftar Menu Makanan</Title>
           <Text c="dimmed" size="sm">Memantau menu yang dibuat oleh Penyedia</Text>
        </div>
        <Button variant="light" leftSection={<IconRefresh size={16}/>} onClick={fetchMenus}>
            Refresh
        </Button>
      </Group>

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Menu & Tanggal</Table.Th>
                <Table.Th>Penyedia (Catering)</Table.Th>
                <Table.Th>Deskripsi</Table.Th>
                <Table.Th>Komposisi</Table.Th>
                <Table.Th>Aksi</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* --- MODAL DETAIL MENU --- */}
      <Modal 
        opened={opened} 
        onClose={close} 
        title={<Text fw={700} size="lg">Detail Menu Makanan</Text>}
        centered
        size="lg"
      >
        {selectedMenu && (
          <Box>
            {/* Informasi Header Menu */}
            <Paper withBorder p="md" bg="gray.0" mb="md" radius="md">
                <Grid>
                    <Grid.Col span={6}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Nama Menu</Text>
                        <Text fw={500} size="lg">{selectedMenu.nama_menu}</Text>
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Tanggal Menu</Text>
                        <Group gap={5}>
                            <IconCalendar size={16} color="gray"/>
                            <Text>
                                {new Date(selectedMenu.tanggal_menu).toLocaleDateString('id-ID', {
                                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                                })}
                            </Text>
                        </Group>
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Penyedia</Text>
                        <Group gap={5}>
                            <IconChefHat size={16} color="gray"/>
                            <Text>{selectedMenu.penyedia?.user?.name || selectedMenu.penyedia?.nama_perusahaan || '-'}</Text>
                        </Group>
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Deskripsi</Text>
                        <Text size="sm" style={{fontStyle: 'italic'}}>"{selectedMenu.deskripsi}"</Text>
                    </Grid.Col>
                </Grid>
            </Paper>

            <Divider my="md" labelPosition="center" label={
                <Group gap={5}><IconLeaf size={14}/><Text size="sm" fw={500}>Komposisi Bahan</Text></Group>
            }/>

            {/* Tabel Bahan Makanan */}
            <Table striped withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Nama Bahan</Table.Th>
                        <Table.Th>Jumlah</Table.Th>
                        <Table.Th>Satuan</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {selectedMenu.bahan_makanans && selectedMenu.bahan_makanans.length > 0 ? (
                        selectedMenu.bahan_makanans.map((bahan, idx) => (
                            <Table.Tr key={idx}>
                                <Table.Td>{bahan.nama_bahan}</Table.Td>
                                <Table.Td>{bahan.jumlah}</Table.Td>
                                <Table.Td>{bahan.satuan}</Table.Td>
                            </Table.Tr>
                        ))
                    ) : (
                        <Table.Tr>
                            <Table.Td colSpan={3} align="center" c="dimmed">
                                Tidak ada data bahan makanan.
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>

            <Group justify="flex-end" mt="md">
                <Button onClick={close}>Tutup</Button>
            </Group>
          </Box>
        )}
      </Modal>
    </>
  );
}